import { connectHubspot, connectGhl, connectPipedrive, connectSalesforce, connectQuickbooks, connectXero, connectQuickbooksAlt, disconnectSystem } from '../../services';
import { useAppContext } from '../../context/AppContext';
import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { ConnectWiseModal } from '../Modals';
import hub from '../../assets/images/hub.png';
import ghl from '../../assets/images/ghl.png';
import pipeImg from '../../assets/images/pipe.png';
import salesforceImg from '../../assets/images/force.png';
import wiseImg from '../../assets/images/cw.png';
import qbo from '../../assets/images/qbo.png';
import xero from '../../assets/images/xero.png';

const CRM_OPTIONS = [
  { id: 'hubspot', label: 'HubSpot', img: hub, connect: connectHubspot },
  { id: 'ghl', label: 'GoHighLevel', img: ghl, connect: connectGhl },
  { id: 'pipedrive', label: 'Pipedrive', img: pipeImg, connect: connectPipedrive },
  { id: 'salesforce', label: 'Salesforce', img: salesforceImg, connect: connectSalesforce },
  { id: 'wise', label: 'ConnectWise', img: wiseImg, authType: 'api_key' },
];

const ACCT_OPTIONS = [
  { id: 'qbo', label: 'QuickBooks', img: qbo, connect: connectQuickbooks },
  { id: 'xero', label: 'Xero', img: xero, connect: connectXero },
];

function resolveConnectedCrm(crmConnected) {
  if (!crmConnected) return null;
  const key = String(crmConnected).toUpperCase();
  if (key.includes('PIPE')) return CRM_OPTIONS.find((c) => c.id === 'pipedrive');
  if (key.includes('SF') || key.includes('FORCE')) return CRM_OPTIONS.find((c) => c.id === 'salesforce');
  if (key.includes('WISE')) return CRM_OPTIONS.find((c) => c.id === 'wise');
  if (key.includes('GHL')) return CRM_OPTIONS.find((c) => c.id === 'ghl');
  return CRM_OPTIONS.find((c) => c.id === 'hubspot');
}

function resolveConnectedAcct(acctConnected) {
  if (!acctConnected) return null;
  if (String(acctConnected).toUpperCase() === 'XERO') {
    return ACCT_OPTIONS.find((a) => a.id === 'xero');
  }
  return ACCT_OPTIONS.find((a) => a.id === 'qbo');
}

function IntegrationOptionButton({ img, label, onClick, disabled }) {
  return (
    <button
      type="button"
      className="integration-button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`Connect ${label}`}
    >
      <img src={img} alt="" className="integration-button-logo" />
      <span className="integration-button-label">{label}</span>
    </button>
  );
}

function ConnectedCard({ option, description, onDisconnect, onReconnect, loading, disconnectLoading }) {
  return (
    <div className="connect-card is-connected">
      <div className="connect-card-info">
        <div className="connect-card-icon">
          <img src={option.img} alt="" width={44} height={44} />
        </div>
        <div className="connect-card-text">
          <strong>{option.label}</strong>
          <span>{description}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={onReconnect}
          disabled={loading}
        >
          {loading ? 'Connecting…' : 'Re-connect'}
        </button>
        <button
          type="button"
          className="connect-btn connected"
          onClick={onDisconnect}
          disabled={disconnectLoading}
        >
          {disconnectLoading ? 'Disconnecting…' : (
            <>
              <span className="connect-btn-label-default">✓ Connected</span>
              <span className="connect-btn-label-hover">Disconnect</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export const ConnectionSettings = () => {
  const { base_url, token, company, setCompany } = useAppContext();
  const [crmLoading, setCrmLoading] = useState(false);
  const [acctLoading, setAcctLoading] = useState(false);
  const [crmDisconnecting, setCrmDisconnecting] = useState(false);
  const [acctDisconnecting, setAcctDisconnecting] = useState(false);
  const [secAcctDisconnecting, setSecAcctDisconnecting] = useState(false);
  const [wiseModalOpen, setWiseModalOpen] = useState(false);

  useEffect(() => {
    if (company?.company_id) {
      Cookies.set('company_id', company.company_id, { domain: '.slipstreaminvoices.com', path: '/' });
    }
  }, [company?.company_id]);

  const handleCrmConnect = async (crm) => {
    if (crm.authType === 'api_key' || crm.id === 'wise') {
      setWiseModalOpen(true);
      return;
    }

    setCrmLoading(true);
    try {
      const redirectUrl = await crm.connect(base_url, token, 'reconnect');
      window.location.href = redirectUrl;
    } finally {
      setCrmLoading(false);
    }
  };

  const handleAcctConnect = async (connectFn) => {
    setAcctLoading(true);
    try {
      const redirectUrl = await connectFn(base_url, token, 'reconnect');
      window.location.href = redirectUrl;
    } finally {
      setAcctLoading(false);
    }
  };

  const handleDisconnect = async (system, setDisconnecting) => {
    setDisconnecting(true);
    try {
      await disconnectSystem(base_url, token, company, setCompany, system);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleWiseSuccess = (data) => {
    setWiseModalOpen(false);
    setCompany({
      ...company,
      crm_connected: data.crm_connected || 'WISE',
    });
  };

  if (!company) {
    return <p className="form-hint">Loading connection settings…</p>;
  }

  const connectedCrm = resolveConnectedCrm(company.crm_connected);
  const connectedAcct = resolveConnectedAcct(company.acct_connected);

  return (
    <div>
      <div className="step-header">
        <h2>Connections</h2>
        <p>Manage how Slipstream connects to your CRM and accounting software.</p>
      </div>

      <p className="connect-section-title">CRM</p>
      {connectedCrm ? (
        <div className="connect-cards" style={{ marginBottom: '28px' }}>
          <ConnectedCard
            option={connectedCrm}
            description="Connected — deal, contact & invoice data"
            onReconnect={() => handleCrmConnect(connectedCrm)}
            onDisconnect={() => handleDisconnect('crm', setCrmDisconnecting)}
            loading={crmLoading}
            disconnectLoading={crmDisconnecting}
          />
        </div>
      ) : (
        <div className="integration-options">
          {CRM_OPTIONS.map((crm) => (
            <IntegrationOptionButton
              key={crm.id}
              img={crm.img}
              label={crm.label}
              onClick={() => handleCrmConnect(crm)}
              disabled={crmLoading}
            />
          ))}
        </div>
      )}

      <p className="connect-section-title">Accounting</p>
      {connectedAcct ? (
        <div className="connect-cards">
          <ConnectedCard
            option={connectedAcct}
            description="Connected — customers & invoices"
            onReconnect={() => handleAcctConnect(connectedAcct.connect)}
            onDisconnect={() => handleDisconnect('acct', setAcctDisconnecting)}
            loading={acctLoading}
            disconnectLoading={acctDisconnecting}
          />
        </div>
      ) : (
        <div className="integration-options">
          {ACCT_OPTIONS.map((acct) => (
            <IntegrationOptionButton
              key={acct.id}
              img={acct.img}
              label={acct.label}
              onClick={() => handleAcctConnect(acct.connect)}
              disabled={acctLoading}
            />
          ))}
        </div>
      )}

      {company.sec_acct_connected === 'QBO' && (
        <>
          <p className="connect-section-title" style={{ marginTop: '28px' }}>Secondary QuickBooks</p>
          <div className="connect-cards">
            <ConnectedCard
              option={{ id: 'qbo-secondary', label: 'QuickBooks', img: qbo, connect: connectQuickbooksAlt }}
              description="Connected secondary company"
              onReconnect={() => handleAcctConnect(connectQuickbooksAlt)}
              onDisconnect={() => handleDisconnect('acct-secondary', setSecAcctDisconnecting)}
              loading={acctLoading}
              disconnectLoading={secAcctDisconnecting}
            />
          </div>
        </>
      )}

      <ConnectWiseModal
        isOpen={wiseModalOpen}
        onClose={() => setWiseModalOpen(false)}
        onSuccess={handleWiseSuccess}
      />
    </div>
  );
};
